import { Field, NativeSelect } from '@chakra-ui/react'
import { FormErrors } from 'commons/components/layout/FormErrors'
import { Label } from 'commons/components/layout/Label'
import { Section } from 'commons/components/layout/Section'
import { alphaTwoFormattedCountries } from 'commons/constants/countries'
import { LANGUAGES } from 'commons/constants/language'
import { BgColors, TextColors } from 'commons/design-system/tokens'
import { FormStep } from 'commons/types/paymentTypes'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

type CountrySectionProps = {
	errors: {
		country?: string
	}
	isLoading: boolean
	step: FormStep
	isReadOnly: boolean
	country: string
	onCountryChange?: (value: string) => void
	onClearError?: (field: 'country') => void
}

export const CountrySection = ({
	errors,
	isLoading,
	step,
	isReadOnly,
	country,
	onCountryChange,
	onClearError
}: CountrySectionProps) => {
	const { t, i18n } = useTranslation()
	const getBorder = (isError: boolean) =>
		isError ? `1px solid ${TextColors.STATUS_ERROR}` : `1px solid ${BgColors.Z3}`

	const sortedCountries = useMemo(() => {
		return [...alphaTwoFormattedCountries].sort((a, b) => {
			const nameA = i18n.language === LANGUAGES.FRENCH ? a.nameFr : a.nameEn
			const nameB = i18n.language === LANGUAGES.FRENCH ? b.nameFr : b.nameEn
			return nameA.localeCompare(nameB)
		})
	}, [i18n.language])

	return (
		<Field.Root invalid={!!errors.country}>
			<Section px={0} pt={2} pb={0}>
				<Label>{t('phone_form.verify_identity_manual_country_label')}</Label>
				<NativeSelect.Root disabled={isLoading || step === FormStep.STEP_BIRTHDATE || isReadOnly}>
					<NativeSelect.Field
						autoComplete='country'
						name='country'
						fontFamily='Montserrat'
						fontWeight='500'
						border={getBorder(!!errors.country)}
						borderRadius='6px'
						py={2}
						height='2.5rem'
						value={country}
						onChange={(e) => {
							onCountryChange?.(e.target.value)
							if (errors.country) {
								onClearError?.('country')
							}
						}}
						_focus={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
						_focusVisible={{ outline: 'none', borderColor: 'blue.500', boxShadow: 'none' }}
						bg={isReadOnly ? BgColors.Z4 : BgColors.Z5}
						placeholder={t('phone_form.verify_identity_manual_country_placeholder')}
					>
						{sortedCountries.map((c) => (
							<option key={c.code} value={c.code}>
								{i18n.language === LANGUAGES.FRENCH ? c.nameFr : c.nameEn}
							</option>
						))}
					</NativeSelect.Field>
					<NativeSelect.Indicator />
				</NativeSelect.Root>
				{errors.country && <FormErrors withoutBorder errors={[errors.country]} />}
			</Section>
		</Field.Root>
	)
}
